/* eslint-disable */
import { Canvas, Browser, DomEvent } from "leaflet";

function TRUE_FN() {
    return true;
}

/**
 * Leaflet's Canvas renderer only binds mouse events (mousedown, click, etc.)
 * on the <canvas> element — not touch or pointer events. Since the canvas sets
 * `_leaflet_disable_events = true`, touch/pointer events that bubble to the map
 * container are ignored. This means Canvas paths never receive `mousedown` from
 * touch input, and leaflet-path-drag's drag handlers never trigger.
 *
 * We patch `_initContainer` to bind `pointerdown` to a custom
 * `_onTouchDragStart` handler that:
 * 1. Normalizes touch coordinates (TouchEvents carry clientX/Y on touches[0])
 * 2. Runs the same hit-test as `_onClick`
 * 3. Fires the event on the matched path as Leaflet event type `mousedown`,
 *    which is what leaflet-path-drag listens for
 */
const originalInitContainer = Canvas.prototype._initContainer;
Canvas.include({
    _initContainer: function () {
        originalInitContainer.call(this);
        DomEvent.on(
            this._container,
            "pointerdown",
            this._onTouchDragStart,
            this,
        );
    },

    _onTouchDragStart: function (e) {
        // Skip mouse-type pointer events — they're already handled by the
        // Canvas renderer's built-in mousedown → _onClick flow.
        if (e.pointerType === "mouse") return;

        const touch = e.touches?.[0] ?? e;
        const point = this._map.mouseEventToLayerPoint(touch);
        let clickedLayer;

        for (let order = this._drawFirst; order; order = order.next) {
            const layer = order.layer;
            if (layer.options.interactive && layer._containsPoint(point)) {
                clickedLayer = layer;
            }
        }

        if (clickedLayer) {
            // _fireDOMEvent reads clientX/clientY from the event to compute
            // latlng. TouchEvents don't have these directly (they're on
            // touches[0]), so we create a proxy with normalized coordinates.
            const normalized = new Proxy(e, {
                get(target, prop) {
                    if (prop === "clientX") return touch.clientX;
                    if (prop === "clientY") return touch.clientY;
                    const val = Reflect.get(target, prop);
                    return typeof val === "function" ? val.bind(target) : val;
                },
            });
            this._map._fireDOMEvent(normalized, "mousedown", [clickedLayer]);
        }
    },

    /**
     * Do nothing
     * @param  {L.Path} layer
     */
    _resetTransformPath: function (layer) {
        if (!this._containerCopy) return;
        delete this._containerCopy;

        if (layer._containsPoint_) {
            layer._containsPoint = layer._containsPoint_;
            delete layer._containsPoint_;

            this._requestRedraw(layer);
        }
    },

    /**
     * Algorithm outline:
     *
     * 1. pre-transform - clear the path out of the canvas, copy canvas state
     * 2. at every frame:
     *    2.1. save
     *    2.2. redraw the canvas from saved one
     *    2.3. transform
     *    2.4. draw path
     *    2.5. restore
     * 3. Repeat
     *
     * @param  {L.Path}         layer
     * @param  {Array.<Number>} matrix
     */
    transformPath: function (layer, matrix) {
        const ctx = this._ctx;

        const m = Browser.retina ? 2 : 1;

        const bounds = this._bounds;
        const size = bounds.getSize();
        const pos = bounds.min;

        if (!this._containerCopy) {
            // get copy of all rendered layers
            this._containerCopy = document.createElement("canvas");
            const copyCtx = this._containerCopy.getContext("2d");
            // document.body.appendChild(copy);
            this._containerCopy.width = size.x * m;
            this._containerCopy.height = size.y * m;
            this._removePath(layer);
            layer.subLayers?.forEach((l) => this._removePath(l));
            this._redraw();

            copyCtx.drawImage(this._container, 0, 0);
            layer.subLayers?.forEach((l) => this._initPath(l));
            this._initPath(layer);

            // avoid flickering because of the 'mouseover's
            layer._containsPoint_ = layer._containsPoint;
            layer._containsPoint = TRUE_FN;
        }
        ctx.save();
        ctx.clearRect(pos.x, pos.y, size.x * m, size.y * m);

        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.restore();
        ctx.save();
        ctx.drawImage(this._containerCopy, pos.x, pos.y, size.x, size.y);

        ctx.transform.apply(ctx, matrix);

        // now draw one layer only
        this._drawing = true;
        layer.subLayers?.forEach((l) => l._updatePath());
        layer._updatePath();
        this._drawing = false;

        ctx.restore();
    },
});
